import { JobStatusBadge } from '@/components/badges/JobStatusBadge';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { useRowClick } from '@/lib/navigation';
import { MetadataItem } from '@/components/MetadataItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { taskGenerationJobRunState, taskGenerationJobRunTasksState } from '@/state/taskGenerationJobRuns.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { TaskGenerationJobRunId } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const TaskGenerationJobRunDetailsPage: FC = () => {
    const { jobId } = useParams<{ jobId: TaskGenerationJobRunId }>();
    const navigate = useNavigate();
    const [run, runLoadable] = useLoadableAtomValue(taskGenerationJobRunState(jobId));

    if (runLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading job run details…</div>
                </CardContent>
            </Card>
        );
    }

    if (runLoadable.state === 'hasError' || !run) {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-destructive">
                        {runLoadable.state === 'hasError'
                            ? `Error loading job run: ${
                                  runLoadable.error instanceof Error
                                      ? runLoadable.error.message
                                      : String(runLoadable.error)
                              }`
                            : 'Job run not found'}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader className="flex flex-row items-center gap-3 border-b-1 pb-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <CardTitle className="text-xl">Task Generation Job Run</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <MetadataItem label="Run ID" value={<IdWithCopy id={run.id} variant="mono" />} />
                            <MetadataItem label="Status" value={<JobStatusBadge status={run.status} />} />
                            <MetadataItem
                                label="Requested At"
                                value={run.requestedAt ? new Date(run.requestedAt).toLocaleString() : '-'}
                            />
                            <MetadataItem
                                label="Finished At"
                                value={run.finishedAt ? new Date(run.finishedAt).toLocaleString() : '-'}
                            />
                            {run.subscriptionCount !== undefined && (
                                <MetadataItem label="Subscriptions processed" value={run.subscriptionCount} />
                            )}
                            {run.taskCount !== undefined && (
                                <MetadataItem label="Tasks created" value={run.taskCount} />
                            )}
                        </div>
                        {run.errorMessage ? (
                            <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                                <div className="text-sm font-semibold text-destructive mb-2">Error Message</div>
                                <div className="text-sm text-foreground whitespace-pre-wrap">{run.errorMessage}</div>
                            </div>
                        ) : null}
                    </div>
                </CardContent>
            </Card>

            <CreatedTasksCard runId={jobId} />

            <Card>
                <CardHeader>
                    <CardTitle>Job Object</CardTitle>
                    <CardDescription>The complete job run payload stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={run} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const CreatedTasksCard: FC<{ runId: TaskGenerationJobRunId | undefined }> = ({ runId }) => {
    const onRowClick = useRowClick();
    const [tasks, tasksLoadable] = useLoadableAtomValue(taskGenerationJobRunTasksState(runId), []);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Created Tasks</CardTitle>
                <CardDescription>
                    Tasks generated during this job run ({tasksLoadable.state === 'loading' ? '…' : tasks.length}).
                </CardDescription>
            </CardHeader>
            <CardContent>
                {tasksLoadable.state === 'loading' ? (
                    <div className="section-description">Loading tasks…</div>
                ) : tasksLoadable.state === 'hasError' ? (
                    <div className="text-destructive text-sm">
                        Error loading tasks:{' '}
                        {tasksLoadable.error instanceof Error
                            ? tasksLoadable.error.message
                            : String(tasksLoadable.error)}
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="section-description">No tasks were created in this job run.</div>
                ) : (
                    <Table className="not-prose">
                        <TableHeader className="bg-secondary">
                            <TableRow>
                                <TableHead>Task ID</TableHead>
                                <TableHead>Home ID</TableHead>
                                <TableHead>Subscription ID</TableHead>
                                <TableHead>Task Date</TableHead>
                                <TableHead>Created At</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tasks.map((task) => (
                                <TableRow
                                    key={task.id}
                                    className="cursor-pointer"
                                    onClick={onRowClick(`/directory/homes/${task.homeId}/tasks/${task.id}`)}
                                >
                                    <TableCell className="font-mono text-xs">{task.id}</TableCell>
                                    <TableCell className="font-mono text-xs">{task.homeId}</TableCell>
                                    <TableCell className="font-mono text-xs">{task.subscriptionId ?? '-'}</TableCell>
                                    <TableCell className="text-sm">{task.taskDate}</TableCell>
                                    <TableCell className="text-sm">
                                        {new Date(task.createdAt).toLocaleString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
